/* eslint-disable @next/next/no-img-element */
"use client"

import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from "@/lib/constants/legal"
import { ServiceModules } from "@/lib/constants/services"
import { pushTrialSignupSuccess } from "@/lib/utils/trial-signup-event"
import { useAccount } from "@/lib/hooks/store/useAccount"
import { useAPI } from "@aw/lib"
import { Button, Form, Heading, Input, Label } from "@aw/ui"
import Link from "next/link"
import { useRouter } from "next/navigation"
import React, { ChangeEvent, useEffect, useState } from 'react'
import { signupSchema, type SignupFormValues } from "../utils/validation"
import { OlameeLogo } from "@/components/shared/olamee-logo"

type Props = {
    /** Takes no uid: `POST /register` returns `204` with no body to read one from. */
    redirectionHandler: () => void
}
function SignupForm({ redirectionHandler }: Props) {
    const { user } = useAccount()
    const router = useRouter()
    useEffect(() => {
        if (user) {
            if (user?.password_added) {
                router.push(`${process.env.NEXT_PUBLIC_ORG_URL}/welcome`)
            }
        }
    }, [user, router])

    const [state, setState] = useState<SignupFormValues>({
        fname: "",
        lname: "",
        organization_name: "",
        email: '',
        password: '',
        password_confirmation: "",
    })
    const [errors, setErrors] = useState<Record<string, string[]>>({})

    const api = useAPI()
    const register = api.post("/register", { service: ServiceModules.AUTH })


    function submitForm(event: React.FormEvent) {
        event.preventDefault()

        // Validate form data
        const result = signupSchema.safeParse(state)

        if (!result.success) {
            const newErrors: Record<string, string[]> = {};
            result.error.issues.forEach(issue => {
                const field = issue.path.join('.');
                if (!newErrors[field]) {
                    newErrors[field] = [];
                }
                newErrors[field].push(issue.message);
            });
            setErrors(newErrors);
            return;
        }

        // Clear errors on successful validation
        setErrors({});

        register.mutate(state, {
            // No response body to read — the endpoint returns `204`. The previous annotation
            // asserted a shape that never existed, so the optional chaining silently yielded
            // `undefined` and the redirect became `/o/undefined/dashboard`.
            onSuccess() {
                pushTrialSignupSuccess(redirectionHandler)
            },
            onError(err) {
                console.log(err)
            }
        })
    }


    return (


        <div className="h-screen w-full flex items-start">
            <div className="w-0 lg:w-1/2 lg:px-4 h-full flex items-center justify-center"
                style={{
                    background: "linear-gradient(24deg, #29E6BF -1.73%, #7F5FFF 93.44%)",
                }}
            >
                <div className="w-[50rem]">

                    <div className="text-gunmetal-lightest font-montserrat text-[1.5rem] font-normal">
                        Find. Onboard. Manage.
                    </div>

                    <div className="text-[3.5rem] font-semibold leading-[4rem] text-gunmetal-lightest font-montserrat">
                        Full service global employment solution
                    </div>
                    <div className="text-gunmetal-lightest font-montserrat text-[1.25rem] leading-7">
                        Harness global talent with our all-in-one solution! Easily find and assess international workers, monitor productivity, and manage payroll seamlessly.
                    </div>
                    <div className="mt-16">
                        <img src={"/vectors/login-vector.svg"} alt="login" />
                    </div>
                </div>
            </div>
            <div className="w-full xl:w-1/2 p-2 xl:p-0 h-full flex items-center justify-center bg-gunmetal-50 dark:bg-gunmetal-50-dark">
                <div className="w-[30rem] flex flex-col gap-4">
                    <div className="flex w-full select-none">

                        <div className="h-[3.5rem] w-auto">

                            <OlameeLogo className="h-full" />
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 mt-3" >
                        <Heading size="h3" className="text-left !font-bold text-[2.25rem] text-gunmetal-800 dark:text-gunmetal-800-dark">
                            Create your account
                        </Heading>
                        <div className="text-gunmetal-500 dark:text-gunmetal-500-dark text-base">
                            Enter your email to continue
                        </div>
                    </div>
                    <Form.FormErrors state={register?.error?.response?.data} className="mb-2 -mt-6" />
                    <form onSubmit={submitForm} className="font-karla flex flex-col gap-4 w-full">
                        <div className="w-full flex items-center gap-4">
                            <div className="w-1/2 flex flex-col gap-1">
                                <Label htmlFor="fname" required >
                                    First Name
                                </Label>
                                <Input
                                    id="fname"
                                    name="fname"
                                    type="text"
                                    placeholder="John"
                                    value={state?.fname}
                                    disabled={register.isPending}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                        setState((prev) => { return { ...prev, fname: event.target.value } })
                                        setErrors(prev => ({ ...prev, fname: [] }))
                                    }}
                                    autoFocus
                                    errorMessage={errors['fname']?.[0]}
                                    hasError={!!errors['fname']?.length}
                                    onErrorClear={() => setErrors(prev => ({ ...prev, fname: [] }))}
                                />
                            </div>
                            <div className="w-1/2 flex flex-col gap-1">
                                <Label htmlFor="lname" required >
                                    Last Name
                                </Label>
                                <Input
                                    id="lname"
                                    name="lname"
                                    type="text"
                                    placeholder="Doe"
                                    value={state?.lname}
                                    disabled={register.isPending}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                        setState((prev) => { return { ...prev, lname: event.target.value } })
                                        setErrors(prev => ({ ...prev, lname: [] }))
                                    }}
                                    errorMessage={errors['lname']?.[0]}
                                    hasError={!!errors['lname']?.length}
                                    onErrorClear={() => setErrors(prev => ({ ...prev, lname: [] }))}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-1 w-full">
                            <Label htmlFor="organization_name" required >
                                Organization Name
                            </Label>
                            <Input
                                id="organization_name"
                                name="organization_name"
                                type="text"
                                placeholder="Company Name"
                                value={state?.organization_name}
                                disabled={register.isPending}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    setState((prev) => { return { ...prev, organization_name: event.target.value } })
                                    setErrors(prev => ({ ...prev, organization_name: [] }))
                                }}
                                errorMessage={errors['organization_name']?.[0]}
                                hasError={!!errors['organization_name']?.length}
                                onErrorClear={() => setErrors(prev => ({ ...prev, organization_name: [] }))}
                            />
                        </div>
                        <div className="flex flex-col gap-1 w-full">
                            <Label htmlFor="email" required>
                                Email address
                            </Label>
                            <Input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="hello@example.com"
                                value={state?.email}
                                disabled={register.isPending}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    setState((prev) => { return { ...prev, email: event.target.value } })
                                    setErrors(prev => ({ ...prev, email: [] }))
                                }}
                                errorMessage={errors['email']?.[0]}
                                hasError={!!errors['email']?.length}
                                onErrorClear={() => setErrors(prev => ({ ...prev, email: [] }))}
                            />
                        </div>
                        <div className="flex flex-col gap-1 w-full">
                            <Label htmlFor="password" required>
                                Password
                            </Label>
                            <Input
                                type="password"
                                id="password"
                                name="password"
                                value={state?.password}
                                placeholder="At least 8 characters"
                                disabled={register.isPending}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    setState((prev) => { return { ...prev, password: event.target.value } })
                                    setErrors(prev => ({ ...prev, password: [] }))
                                }}
                                errorMessage={errors['password']?.[0]}
                                hasError={!!errors['password']?.length}
                                onErrorClear={() => setErrors(prev => ({ ...prev, password: [] }))}
                            />
                        </div>
                        <div className="flex flex-col gap-1 w-full">
                            <Label htmlFor="password_confirmation" required>
                                Confirm Password
                            </Label>
                            <Input
                                type="password"
                                id="password_confirmation"
                                name="password_confirmation"
                                value={state?.password_confirmation}
                                placeholder="Re-enter your password"
                                disabled={register.isPending}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    setState((prev) => { return { ...prev, password_confirmation: event.target.value } })
                                    setErrors(prev => ({ ...prev, password_confirmation: [] }))
                                }}
                                errorMessage={errors['password_confirmation']?.[0]}
                                hasError={!!errors['password_confirmation']?.length}
                                onErrorClear={() => setErrors(prev => ({ ...prev, password_confirmation: [] }))}
                            />
                        </div>

                        <Button
                            id="create-account-btn"
                            size="md"
                            type={"submit"}
                            color="purple"
                            loading={register.isPending}
                        >
                            Create Account
                        </Button>
                        <p>
                            By signing up, you agree to the Olamee <Link
                                href={TERMS_AND_CONDITIONS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 cursor-pointer font-bold"
                            >Terms & Conditions</Link> and <Link
                                href={PRIVACY_POLICY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 font-bold cursor-pointer"
                            >Privacy Policy</Link>.
                        </p>

                    </form>
                </div>
            </div>
        </div >
    )
}

export default SignupForm


